/**
 * @file Region Event Manager
 * @description Event handling and binding for region component
 */

import { EventManager } from "../../core/EventManager.js";
import { SELECTORS, EVENTS } from "./constants.js";
import {
  getElement,
  getElementsBySelector,
  getElementById,
  getElementBySelector,
} from "./dom-manager.js";
import { setEventManager, getEventManager } from "./state.js";
import {
  highlightRegionOnMap,
  highlightRegionItem,
  removeRegionHighlight,
  removeRegionItemHighlight,
  setActiveRegionItem,
} from "./highlight-manager.js";
import {
  showRegions,
  resetRegionDisplay,
  setProvinceListingLoading,
  setRegionDescriptionLoading,
  handleBackButton,
} from "./ui-manager.js";
import { handleRegionMouseOver, handleRegionMouseLeave } from "./tooltip-manager.js";
import { getTermIDFromSlug, findCorrespondingNavLink } from "./utils.js";
import { expandBottomSheet, collapseBottomSheet } from "../../utils/bottom-sheet-manager.js";

/**
 * Initialize Event Manager
 */
export async function initializeEventManager() {
  const eventManager = EventManager.getInstance();
  if (!eventManager.isInitialized) {
    await eventManager.init();
  }
  setEventManager(eventManager);
  return eventManager;
}

/**
 * Unified region hover handler
 * @param {Event} e - Mouse event
 * @param {string} source - Source of the event ('link' or 'map')
 */
function handleRegionHover(e, source) {
  // Extract event data from EventManager or use direct event
  const event = e.event || e;
  const element = e.element || event.currentTarget;

  // For map regions, validate that this is a vietnam-map region, not a child-map region
  if (source === "map") {
    const isVietnamMapRegion = element.closest(SELECTORS.VIETNAM_MAP_REGIONS);
    if (!isVietnamMapRegion) {
      return; // Skip child-map regions
    }
  }

  const regionName = element.dataset.region;
  if (!regionName) return;

  if (source === "link") {
    // Hover on navigation link → highlight map region
    highlightRegionOnMap(regionName);
  } else if (source === "map") {
    // Hover on map region → highlight navigation link
    highlightRegionItem(regionName);
  }
}

/**
 * Unified region hover leave handler
 * @param {Event} e - Mouse event
 * @param {string} source - Source of the event ('link' or 'map')
 */
function handleRegionHoverLeave(e, source) {
  // Extract event data from EventManager or use direct event
  const event = e.event || e;
  const element = e.element || event.currentTarget;

  if (source === "link") {
    // Leave navigation link → remove map highlight
    removeRegionHighlight();
  } else if (source === "map") {
    // Leave map region → remove navigation highlight
    removeRegionItemHighlight();
  }
}

/**
 * Handle zone mouse over event
 * @param {Event} e - Mouse event
 */
export function handleZoneMouseOver(e) {
  return handleRegionHover(e, "map");
}

/**
 * Handle zone mouse leave event
 * @param {Event} e - Mouse event
 */
export function handleZoneMouseLeave(e) {
  return handleRegionHoverLeave(e, "map");
}

/**
 * Unified region selection handler
 * @param {Event} e - Click event
 * @param {string} source - Source of the event ('link' or 'zone')
 */
async function handleRegionSelection(e, source) {
  // Extract event data from EventManager or use direct event
  const event = e.event || e;
  const element = e.element || event.currentTarget;

  // Zone-specific validation
  if (source === "zone") {
    // Validate that this is a vietnam-map region, not a child-map region
    const isVietnamMapRegion = element.closest(SELECTORS.VIETNAM_MAP_REGIONS);
    if (!isVietnamMapRegion) {
      return; // Skip child-map regions
    }
  }

  event.preventDefault();
  const regionName = element.dataset.region;
  const regionID = element.dataset.id; // Get term ID from data-id attribute

  if (regionName && regionID) {
    try {
      // Remove active class from all region items
      const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
      allItems.forEach((item) => item.classList.remove("active"));

      // Add active class to clicked region item
      element.classList.add("active");

      // NEW: Update region-select dropdown to match selected region
      const regionSelect = getElement("regionSelect");
      if (regionSelect) {
        // Remove selected from all options
        Array.from(regionSelect.options).forEach((option) => {
          option.selected = false;
        });

        // Find and select the corresponding option
        const targetOption = regionSelect.querySelector(`option[data-region-id="${regionID}"]`);
        if (targetOption) {
          targetOption.selected = true;
        }
      }

      await showRegions(regionID, regionName); // Use term ID for API calls, slug for child map

      // NEW: Expand bottom sheet when region is selected
      expandBottomSheet();
    } catch (error) {
      console.error(`Error handling region selection (${source}):`, error);
    }
  }
}

/**
 * Handle region link click (for .mps-regions links)
 * @param {Event} e - Click event
 */
export async function handleRegionLinkClick(e) {
  return handleRegionSelection(e, "link");
}

/**
 * Handle zone click (for map regions)
 * @param {Event} e - Click event
 */
export async function handleZoneClick(e) {
  return handleRegionSelection(e, "zone");
}

/**
 * Handle region click event (for map regions)
 * @param {Event} e - Click event
 */
export async function handleRegionClick(e) {
  // Validate event target before using closest
  if (!e.target || typeof e.target.closest !== "function") {
    return;
  }

  // Only handle regions within vietnam-map, ignore child-map regions
  const region = e.target.closest(SELECTORS.VIETNAM_MAP_REGIONS);
  if (!region) return;

  const regionID = region.id;

  // Find corresponding navigation link
  const correspondingNavLink = findCorrespondingNavLink(regionID, SELECTORS.REGION_ITEMS);
  if (!correspondingNavLink) {
    console.error("No corresponding navigation link found for region:", regionID);
    return;
  }

  // Trigger click on the corresponding navigation link
  // This will use the existing logic for navigation links which already handles term ID correctly
  correspondingNavLink.click();
}

/**
 * Handle back button click in child maps
 */
export async function handleBackButtonClick(e) {
  // Extract event data from EventManager or use direct event
  const event = e?.event || e;

  try {
    await resetRegionDisplay();

    // NEW: Collapse bottom sheet when going back
    collapseBottomSheet();
  } catch (error) {
    console.error("Error handling back button click:", error);
  }
}

/**
 * Handle turn back button click
 */
export async function handleTurnBackButtonClick(e) {
  e.preventDefault();
  await handleBackButton();
}

/**
 * Handle region select change
 */
export async function handleRegionSelectChange(e) {
  e.preventDefault();

  const selectedOption = e.target.options[e.target.selectedIndex];
  const regionID = selectedOption.getAttribute("data-region-id");
  const regionSlug = selectedOption.getAttribute("data-region-slug");

  const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  allItems.forEach((item) => item.classList.remove("active"));
  e.target.classList.add("active");

  // Set loading state
  setProvinceListingLoading();
  setRegionDescriptionLoading();

  // Hide all child maps
  const { hideElement } = await import("./utils.js");
  const allChildMaps = getElementsBySelector(SELECTORS.CHILD_MAPS);
  const mapPromises = Array.from(allChildMaps).map((map) => hideElement(map));
  await Promise.all(mapPromises);

  // Show regions with both term_id and slug
  await showRegions(regionID, regionSlug);

  // NEW: Expand bottom sheet to show new content
  expandBottomSheet();
}

/**
 * Handle province listing change
 */
export function handleProvinceListingChange(e) {
  e.preventDefault();
  window.location.href = e.target.value;
}

/**
 * Handle province mobile nav tab click
 */
export function handleProvinceMobileNavTabClick(e) {
  // Extract event data from EventManager or use direct event
  const event = e.event || e;
  const element = e.element || event.currentTarget;

  event.preventDefault();
  const tabIndex = element.dataset.tab;

  if (tabIndex) {
    // Handle tab switching logic
    const tabElements = document.querySelectorAll(SELECTORS.PROVINCE_MOBILE_NAV + " .tab");
    const tabContents = document.querySelectorAll(".tab-content");

    // Remove active class from all tabs
    tabElements.forEach((tab) => tab.classList.remove("active"));

    // Add active class to clicked tab
    element.classList.add("active");

    // Hide all tab contents
    tabContents.forEach((content) => {
      content.style.display = "none";
    });

    // Show selected tab content
    const selectedContent = document.querySelector(tabIndex);
    if (selectedContent) {
      selectedContent.style.display = "block";

      // Special handling for province-info-full tab
      if (tabIndex === "#province-info-full") {
        selectedContent.classList.add("active");
        const industriesElement = document.getElementById("industries");
        if (industriesElement) {
          industriesElement.classList.add("province-info-actived");
        }
      } else if (tabIndex === "#ip-list") {
        // When switching to list tab, close province info
        const provinceInfoFull = document.getElementById("province-info-full");
        if (provinceInfoFull) {
          provinceInfoFull.classList.remove("active");
        }
        const industriesElement = document.getElementById("industries");
        if (industriesElement) {
          industriesElement.classList.remove("province-info-actived");
        }
      }
    }
  }
}

/**
 * Handle province info full close button click
 * @param {Event} e - Click event
 */
export function handleProvinceInfoFullCloseClick(e) {
  e.preventDefault();

  // Remove active class from province info full
  const provinceInfoFull = document.getElementById("province-info-full");
  if (provinceInfoFull) {
    provinceInfoFull.classList.remove("active");
  }

  // Remove province-info-actived class from industries
  const industriesElement = document.getElementById("industries");
  if (industriesElement) {
    industriesElement.classList.remove("province-info-actived");
  }
}

/**
 * Bind all event listeners using event delegation for better performance
 */
export async function bindEventListeners() {
  // Initialize EventManager if not already done
  let eventManager = getEventManager();
  if (!eventManager) {
    eventManager = await initializeEventManager();
  }

  // Register custom events for region interactions
  eventManager.on(EVENTS.REGION_CLICK, handleRegionLinkClick);
  eventManager.on(EVENTS.REGION_MOUSEOVER, handleZoneMouseOver);
  eventManager.on(EVENTS.REGION_MOUSELEAVE, handleZoneMouseLeave);
  eventManager.on(EVENTS.BACK_BUTTON_CLICK, handleBackButtonClick);
  eventManager.on(EVENTS.PROVINCE_TAB_CLICK, handleProvinceMobileNavTabClick);

  // Use event delegation for dynamic elements
  document.addEventListener("click", (e) => {
    // Validate event target before using closest
    if (!e.target || typeof e.target.closest !== "function") {
      return;
    }

    // Handle .mps-regions items clicks
    const regionItem = e.target.closest(SELECTORS.REGION_ITEMS);
    if (regionItem) {
      eventManager.emit(EVENTS.REGION_CLICK, { event: e, element: regionItem });
    }

    // Handle back buttons
    const backBtn = e.target.closest(SELECTORS.BACK_BTN);
    if (backBtn) {
      eventManager.emit(EVENTS.BACK_BUTTON_CLICK, {
        event: e,
        element: backBtn,
      });
    }

    // Handle province mobile nav tabs
    const navTab = e.target.closest(SELECTORS.PROVINCE_TAB);
    if (navTab) {
      eventManager.emit(EVENTS.PROVINCE_TAB_CLICK, {
        event: e,
        element: navTab,
      });
    }

    // Handle province info full close button clicks
    const closeBtn = e.target.closest("#province-info-full .close");
    if (closeBtn) {
      handleProvinceInfoFullCloseClick(e);
    }
  });

  // Use event delegation for hover events on dynamic elements
  document.addEventListener("mouseover", (e) => {
    // Validate event target before using closest
    if (!e.target || typeof e.target.closest !== "function") {
      return;
    }

    const regionItem = e.target.closest(SELECTORS.REGION_ITEMS);
    if (regionItem) {
      // Use unified hover handler for navigation links
      handleRegionHover({ event: e, element: regionItem }, "link");
    }
  });

  document.addEventListener("mouseleave", (e) => {
    // Validate event target before using closest
    if (!e.target || typeof e.target.closest !== "function") {
      return;
    }

    const regionItem = e.target.closest(SELECTORS.REGION_ITEMS);
    if (regionItem) {
      // Use unified hover leave handler for navigation links
      handleRegionHoverLeave({ event: e, element: regionItem }, "link");
    }
  });

  // Bind events to static form elements
  const regionSelect = getElement("regionSelect");
  const provinceListing = getElement("provinceListing");
  const turnBackBtn = getElement("turnBackBtn");

  regionSelect?.addEventListener("change", handleRegionSelectChange);
  provinceListing?.addEventListener("change", handleProvinceListingChange);
  turnBackBtn?.addEventListener("click", handleTurnBackButtonClick);

  // Bind events specifically to vietnam-map for region interactions
  const vietnamMap = getElement("vietnamMap");

  vietnamMap?.addEventListener("click", handleRegionClick);
  vietnamMap?.addEventListener("mouseover", (e) => {
    // Validate event target before using closest
    if (!e.target || typeof e.target.closest !== "function") {
      return;
    }

    // Only handle regions within vietnam-map, ignore child-map regions
    const region = e.target.closest(SELECTORS.VIETNAM_MAP_REGIONS);
    if (region) {
      // Handle both highlight and tooltip for vietnam-map regions
      handleRegionHover({ event: e, element: region }, "map");
      handleRegionMouseOver({ currentTarget: region });
    }
  });
  vietnamMap?.addEventListener("mouseleave", (e) => {
    // Validate event target before using closest
    if (!e.target || typeof e.target.closest !== "function") {
      return;
    }

    // Only handle regions within vietnam-map, ignore child-map regions
    const region = e.target.closest(SELECTORS.VIETNAM_MAP_REGIONS);
    if (region) {
      // Handle both highlight and tooltip leave for vietnam-map regions
      handleRegionHoverLeave({ event: e, element: region }, "map");
      handleRegionMouseLeave({ currentTarget: region });
    }
  });

  // Keep compatibility with maps-wrapper for other interactions (not region-specific)
  const mapsWrapper = getElement("mapsWrapper");
  const mapWrapper = getElement("mapWrapper");

  mapsWrapper?.addEventListener("click", handleRegionClick);
  mapWrapper?.addEventListener("click", handleRegionClick);

  // Bind events for child-map regions (tooltip support only - no highlighting)
  const childMaps = getElementsBySelector(SELECTORS.CHILD_MAPS);
  childMaps.forEach((childMap) => {
    childMap.addEventListener("mouseover", (e) => {
      // Validate event target before using closest
      if (!e.target || typeof e.target.closest !== "function") {
        return;
      }

      // Handle regions within child-maps (tooltip only)
      const region = e.target.closest(SELECTORS.CHILD_MAP_REGIONS);
      if (region) {
        handleRegionMouseOver({ currentTarget: region });
      }
    });

    childMap.addEventListener("mouseleave", (e) => {
      // Validate event target before using closest
      if (!e.target || typeof e.target.closest !== "function") {
        return;
      }

      // Handle regions within child-maps (tooltip only)
      const region = e.target.closest(SELECTORS.CHILD_MAP_REGIONS);
      if (region) {
        handleRegionMouseLeave({ currentTarget: region });
      }
    });
  });

  // Bind events specifically for .mps-regions links
  const regionItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  regionItems.forEach((item) => {
    item.addEventListener("click", handleRegionLinkClick);
  });
}

/**
 * Unbind all event listeners
 */
export function unbindEventListeners() {
  const eventManager = getEventManager();
  if (eventManager) {
    eventManager.destroy();
  }
}
